import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import moment from 'moment';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import DashboardIcon from '@mui/icons-material/Dashboard';

import { getReservationsState } from '../../../../../features/reservation/get_reservations';
import { DeleteButton } from '../../../../common/button';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_POLISH_DATE_ONLY_FORMAT } from '../../../../../core/constants';
import { deleteReservationNotificationData, generateArrangementNotificationData } from '../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import useNotification from '../../../../../core/hooks/use_notification';
import { deleteReservationAction } from '../../../../../features/reservation/delete_reservation';
import ReservationDialog from './reservation_dialog';
import ReservationStoreStatusFormSelect from './form/reservation_store_status_form_select';
import { getAllHubsState } from '../../../../../features/pop/get_all_hubs';
import { getAllPopsState } from '../../../../../features/pop/get_all_pops';
import { generateArrangementAction } from '../../../../../features/reservation/generate_arrangement';
import ConfirmDialog from '../../../common/confirm_dialog';
import { getReservationStatusColor } from '../../../../common/utils/reservation_utils';
import GenerateReservationDocumentsDialog from './generate_reservation_documents_dialog';
import { ReservationStatusEnum } from '../../../../../core/enums/reservation/reservation';
import ReturnReservationDialog from '../../../common/return_reservation_dialog';

const ReservationsList = ({ refreshReservations }) => {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    const [activeActionId, setActiveActionId] = useState(null);

    const getAllHubs = useSelector(getAllHubsState);
    const getAllPops = useSelector(getAllPopsState);
    const getReservations = useSelector(getReservationsState);

    const dates = [...new Set(...[getReservations?.data?.content?.map(reservation => reservation?.date)])];

    const onDeleteReservation = (id) => {
        setActiveActionId(`delete-${ id }`);
        dispatch(deleteReservationAction(id))
            .then(response => {
                showNotification(deleteReservationNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    refreshReservations();
                }
            })
            .finally(() => setActiveActionId(null));
    };

    const onGenerateDocuments = (date) => {
        setSelectedDate(date);
        setGenerateDialogOpen(true);
    };

    const onGenerateArrangements = (date, recreate) => {
        setActiveActionId(`arrange-${ date }`);
        dispatch(generateArrangementAction({ forDate: date, recreate: recreate }))
            .then(response => {
                showErrorMessage(generateArrangementNotificationData(response));
            })
            .finally(() => setActiveActionId(null));
    };

    return (
        <Box>
            <TableContainer component={ Paper } variant="outlined">
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                            <TableCell>Numer rezerwacji</TableCell>
                            <TableCell align="center">Klient</TableCell>
                            <TableCell align="center">Punkt odbioru</TableCell>
                            <TableCell align="center">Status publiczny</TableCell>
                            <TableCell align="center" sx={ { minWidth: 150 } }>Status wewnętrzny</TableCell>
                            <TableCell align="center" sx={ { width: 140 } }></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            dates?.map((date, index) => (
                                <Fragment key={ index }>
                                    <TableRow sx={ { backgroundColor: 'slate.50' } }>
                                        <TableCell colSpan={ 6 } align="center" sx={ { py: 1 } }>
                                            <Box sx={ {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 2
                                            } }>
                                                <Typography variant="subtitle2" sx={ { fontWeight: 600 } }>
                                                    { moment(date, MISC_DATE_ISO_DATE_ONLY_FORMAT).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) }
                                                </Typography>
                                                {
                                                    !moment(date, MISC_DATE_ISO_DATE_ONLY_FORMAT).isBefore(moment(), 'day') &&
                                                    <IconButton size="small"
                                                                title="Wygeneruj dokumenty magazynowe"
                                                                onClick={ () => onGenerateDocuments(date) }>
                                                        <ContentPasteGoIcon fontSize="small"/>
                                                    </IconButton>
                                                }
                                                <ConfirmDialog
                                                    maxWidth="sm"
                                                    isLoading={ activeActionId === `arrange-${ date }` }
                                                    title={ 'Czy jeżeli istnieje już przyporządkowanie półek to czy system ma rozdysponować je na nowo?' }
                                                    secondaryAction={ () => onGenerateArrangements(date, false) }
                                                    secondaryText={ 'Pobierz istniejące' }
                                                    primaryAction={ () => onGenerateArrangements(date, true) }
                                                    primaryText={ 'Wygeneruje na nowo' }
                                                    displayIcon
                                                    iconTitle="Pobierz dokumenty z rozmieszczeniem produktów"
                                                    icon={ <DashboardIcon fontSize="small"/> }
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {
                                        getReservations?.data?.content?.filter(reservation => moment(date).isSame(moment(reservation?.date)))
                                            ?.map((reservation) => (
                                                <TableRow key={ reservation?.reservationId + date } hover>
                                                    <TableCell sx={ { fontWeight: 500 } }>
                                                        { reservation?.reservationId }
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        { `${ reservation?.customer?.customerId || '' } - ${ reservation?.customer?.name || '' } ${ reservation?.customer?.surname || '' }` }
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        { getAllHubs?.data?.find(hub => hub?.id === reservation?.hubId)?.name } / { getAllPops?.data?.find(pop => pop?.id === reservation?.popId)?.name }
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip size="small" label={ reservation?.displayStatusValue }
                                                              color={ getReservationStatusColor(reservation?.status) }
                                                              variant="outlined"/>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <ReservationStoreStatusFormSelect
                                                            reservationId={ reservation?.reservationId }
                                                            storeStatus={ reservation?.storeStatus }
                                                            refreshReservations={ refreshReservations }
                                                            center
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" justifyContent="flex-end"
                                                               alignItems="center" spacing={ 1 }>
                                                            <ReservationDialog
                                                                reservation={ reservation }
                                                                refreshReservations={ refreshReservations }
                                                            />
                                                            {
                                                                [
                                                                    ReservationStatusEnum.FINISHED.value,
                                                                    ReservationStatusEnum.DELIVERED.value,
                                                                    ReservationStatusEnum.PREPARED.value,
                                                                    ReservationStatusEnum.PARTIALLY_RETURNED.value
                                                                ].includes(reservation?.status) && (
                                                                    <ReturnReservationDialog reservation={ reservation }
                                                                                             refreshList={ refreshReservations }/>
                                                                )
                                                            }
                                                            {
                                                                (reservation?.cancellable) &&
                                                                <DeleteButton
                                                                    onDeleteClick={ () => onDeleteReservation(reservation?.reservationId) }
                                                                    isLoading={ activeActionId === `delete-${ reservation?.reservationId }` }
                                                                />
                                                            }
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                    }
                                </Fragment>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <GenerateReservationDocumentsDialog
                open={ generateDialogOpen }
                onClose={ () => {
                    setGenerateDialogOpen(false);
                    refreshReservations();
                } }
                date={ selectedDate }
            />
        </Box>
    );
};

export default ReservationsList;